import { useConfig } from '../../hooks/useConfig';
import type {
  EacFirstRoundAnswer,
  EacLogItem,
  EacLogStatus,
  EacProcessNode,
  EacProcessPayload,
  EacProcessPayloadSecond,
  EacProcessStreamItem,
  EacSecondRoundAnswer,
  RecommendationDecision,
} from './types';

type EacProcessEnv = 'dev' | 'test';

type EacProcessBasePayload = Pick<
  EacProcessPayload,
  '场景本体ID' | '对象本体ID'
>;

interface EacProcessGlobalConfig {
  env?: string;
  eacProcessOntologyConfigs?: Partial<
    Record<
      EacProcessEnv,
      Partial<Record<'firstRound' | 'secondRound', EacProcessBasePayload>>
    >
  >;
}

const DEFAULT_EAC_PROCESS_ONTOLOGY_CONFIGS: Record<
  EacProcessEnv,
  Record<'firstRound' | 'secondRound', EacProcessBasePayload>
> = {
  dev: {
    firstRound: {
      场景本体ID: '179',
      对象本体ID: 175,
    },
    secondRound: {
      场景本体ID: '180',
      对象本体ID: 175,
    },
  },
  test: {
    firstRound: {
      场景本体ID: '200',
      对象本体ID: 196,
    },
    secondRound: {
      场景本体ID: '201',
      对象本体ID: 196,
    },
  },
};

// 第一阶段过程节点（仅用于预设日志步骤名展示）
const FIRST_ROUND_PROCESS: EacProcessNode = {
  name: '预警检测与风险定级',
  concurrency: '串行',
  instruments: ['本体ID', '项目编号'],
  constructions: ['项目数据', 'EAC预警结果'],
  processes: [
    {
      name: '读取项目数据',
      concurrency: '串行',
      instruments: ['本体ID', '项目编号'],
      constructions: ['项目数据'],
    },
    {
      name: '计算EAC数据',
      concurrency: '串行',
      instruments: ['项目数据'],
      constructions: ['EAC数据'],
    },
    {
      name: '生成预警结果',
      concurrency: '串行',
      instruments: ['EAC数据'],
      constructions: ['EAC预警结果'],
    },
  ],
};

// 第二阶段过程节点（仅用于预设日志步骤名展示）
const SECOND_ROUND_PROCESS: EacProcessNode = {
  name: '预警分析与方案推送',
  concurrency: '串行',
  instruments: ['本体ID', '项目数据', 'EAC预警结果'],
  effects: ['已推荐方案列表'],
  constructions: ['详细纠偏方案'],
  processes: [
    {
      name: '读取纠偏方案',
      concurrency: '串行',
      instruments: ['本体ID'],
      constructions: ['纠偏方案列表'],
    },
    {
      name: '推荐纠偏方案',
      concurrency: '串行',
      instruments: ['项目数据', 'EAC预警结果', '纠偏方案列表'],
      effects: ['已推荐方案列表'],
      constructions: ['详细纠偏方案'],
    },
    {
      name: '通知负责人',
      concurrency: '串行',
      instruments: ['本体ID', 'EAC预警结果', '详细纠偏方案', '已推荐方案列表'],
      processes: [
        {
          name: '获取推送账号',
          concurrency: '串行',
          instruments: ['本体ID', 'EAC预警结果', '已推荐方案列表'],
          constructions: ['推送账号列表'],
        },
        {
          name: '邮件推送',
          concurrency: '并行',
          instruments: ['详细纠偏方案', '推送账号列表'],
        },
        {
          name: '飞书推送',
          concurrency: '并行',
          instruments: ['详细纠偏方案', '推送账号列表'],
        },
      ],
    },
  ],
};

export const AUTO_CHECK_LABEL = '每 3 h自动检测一次';

export function parseEacProcessStreamChunk(
  dataStr: string,
  cache: { current: string }
): EacProcessStreamItem[] {
  cache.current += dataStr;

  const tryParse = (str: string): EacProcessStreamItem | null => {
    try {
      return JSON.parse(str) as EacProcessStreamItem;
    } catch {
      return null;
    }
  };

  const results: EacProcessStreamItem[] = [];
  const lines = cache.current.split('\n');
  cache.current = lines.pop() || '';

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (trimmed.includes('}{')) {
      const parts = trimmed.split('}{');
      for (let i = 0; i < parts.length; i += 1) {
        let part = parts[i];
        if (i === 0) {
          part = `${part}}`;
        } else if (i === parts.length - 1) {
          part = `{${part}`;
        } else {
          part = `{${part}}`;
        }
        const parsed = tryParse(part);
        if (parsed) results.push(parsed);
      }
      continue;
    }

    const parsed = tryParse(trimmed);
    if (parsed) results.push(parsed);
  }

  if (cache.current.trim()) {
    const parsed = tryParse(cache.current.trim());
    if (parsed) {
      results.push(parsed);
      cache.current = '';
    }
  }

  return results;
}

export function getProcessNamesInDisplayOrder(
  nodes?: EacProcessNode[]
): string[] {
  if (!nodes || nodes.length === 0) return [];

  return nodes.flatMap(node => {
    return [node.name, ...getProcessNamesInDisplayOrder(node.processes)];
  });
}

export function createStageLogItems(
  names: string[],
  stage: 1 | 2 | 3
): EacLogItem[] {
  return Array.from(new Set(names)).map(name => ({
    key: `${stage}-${name}`,
    name,
    stage,
    status: 'pending',
  }));
}

export function upsertLogStatus(
  items: EacLogItem[],
  stage: 1 | 2 | 3,
  processName: string,
  status: EacLogStatus
): EacLogItem[] {
  let matched = false;
  let changed = false;
  const nextItems = items.map(item => {
    if (item.stage !== stage || item.name !== processName) {
      return item;
    }

    matched = true;

    const nextStatus = mergeLogStatus(item.status, status);
    if (nextStatus === item.status) {
      return item;
    }

    changed = true;
    return {
      ...item,
      status: nextStatus,
    };
  });

  if (matched || changed) {
    return nextItems;
  }

  return [
    ...items,
    {
      key: `${stage}-${processName}`,
      name: processName,
      stage,
      status,
    },
  ];
}

function mergeLogStatus(
  currentStatus: EacLogStatus,
  nextStatus: EacLogStatus
): EacLogStatus {
  const rank: Record<EacLogStatus, number> = {
    pending: 0,
    running: 1,
    completed: 2,
  };

  return rank[nextStatus] >= rank[currentStatus] ? nextStatus : currentStatus;
}

export function getVisibleLogItems(items: EacLogItem[]): EacLogItem[] {
  const dedupedItems = new Map<string, EacLogItem>();

  items.forEach(item => {
    if (item.status === 'pending') {
      return;
    }

    const dedupeKey = `${item.stage}-${item.name}`;
    const existingItem = dedupedItems.get(dedupeKey);

    if (!existingItem) {
      dedupedItems.set(dedupeKey, item);
      return;
    }

    dedupedItems.set(dedupeKey, {
      ...existingItem,
      status: mergeLogStatus(existingItem.status, item.status),
    });
  });

  return Array.from(dedupedItems.values());
}

export function formatWanValue(value?: number): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  const wanValue = value / 10000;
  if (Number.isInteger(wanValue)) {
    return `${wanValue}万`;
  }
  return `${wanValue.toFixed(1)}万`;
}

export function formatPercent(value?: number): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  const percentText = Number.isInteger(value)
    ? `${value}`
    : value.toFixed(1).replace(/\.0$/, '');
  return `${percentText}%`;
}

export function formatDateTime(date: Date | null): string {
  if (!date) return '--';

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export function getRiskBadgeTone(level?: string): {
  bgColor: string;
  textColor: string;
} {
  switch (level) {
    case '高风险':
      return {
        bgColor: '#fde8ec',
        textColor: '#ff2d55',
      };
    case '中风险':
      return {
        bgColor: '#fff3c8',
        textColor: '#f59e0b',
      };
    case '低风险':
      return {
        bgColor: '#fff7db',
        textColor: '#d97706',
      };
    case '无风险':
    default:
      return {
        bgColor: '#dff1ff',
        textColor: '#5dbb13',
      };
  }
}

export function getRiskTypeTone(level?: string): {
  bgColor: string;
  textColor: string;
} {
  switch (level) {
    case '高风险':
      return {
        bgColor: '#fde8ec',
        textColor: '#ff5a5f',
      };
    case '中风险':
      return {
        bgColor: '#fff3c8',
        textColor: '#f59e0b',
      };
    case '低风险':
      return {
        bgColor: '#fff7db',
        textColor: '#d97706',
      };
    case '无风险':
    default:
      return {
        bgColor: '#dff1ff',
        textColor: '#111827',
      };
  }
}

export function buildRiskTypeText(answer?: EacFirstRoundAnswer | null): string {
  const warning = answer?.EAC预警结果;
  if (!warning) return '--';
  if (warning.风险等级 === '无风险') {
    return warning.风险类型 || '--';
  }
  const overspend = warning.超支比例;
  if (typeof overspend === 'number' && !Number.isNaN(overspend)) {
    return `成本超支 ${formatPercent(overspend)}`;
  }
  return warning.风险类型 || '--';
}

export function buildFirstRoundPayload(projectCode: string): EacProcessPayload {
  return {
    ...getEacProcessBasePayload('firstRound'),
    项目编号: projectCode,
  };
}

export function buildSecondRoundPayload(
  firstRoundAnswer: EacFirstRoundAnswer,
  recommendedList: unknown[] = []
): EacProcessPayloadSecond {
  return {
    ...getEacProcessBasePayload('secondRound'),
    项目数据: deepClone(firstRoundAnswer.项目数据),
    EAC预警结果: deepClone(firstRoundAnswer.EAC预警结果),
    已推荐方案列表: [...recommendedList],
  };
}

// 第一阶段日志步骤名（按展示顺序）
export function getFirstRoundProcessNames(): string[] {
  return getProcessNamesInDisplayOrder(FIRST_ROUND_PROCESS.processes);
}

// 第二阶段日志步骤名（按展示顺序）
export function getSecondRoundProcessNames(): string[] {
  return getProcessNamesInDisplayOrder(SECOND_ROUND_PROCESS.processes);
}

export function parseFirstRoundAnswer(
  content?: string
): EacFirstRoundAnswer | null {
  if (!content) return null;
  try {
    return JSON.parse(content) as EacFirstRoundAnswer;
  } catch {
    return null;
  }
}

export function parseSecondRoundAnswer(
  content?: string
): EacSecondRoundAnswer | null {
  if (!content) return null;
  try {
    return JSON.parse(content) as EacSecondRoundAnswer;
  } catch {
    return null;
  }
}

export function extractAdoptPayload(
  answer: EacSecondRoundAnswer | null
): unknown {
  const rawPayload = answer?.详细纠偏方案?.数据更新要求;
  if (!rawPayload) return null;

  try {
    return JSON.parse(rawPayload);
  } catch {
    return rawPayload;
  }
}

export function getRecommendationStatusText(
  decision: RecommendationDecision
): string | null {
  if (decision === 'adopted') return '用户已采纳';
  if (decision === 'rejected') return '用户已拒绝';
  return null;
}

export function isRiskFree(answer: EacFirstRoundAnswer | null): boolean {
  return answer?.EAC预警结果?.风险等级 === '无风险';
}

export function hasRisk(answer: EacFirstRoundAnswer | null): boolean {
  return !!answer && !isRiskFree(answer);
}

export function getProcessStatusFromStreamItem(
  item: EacProcessStreamItem
): { processName: string; status: EacLogStatus } | null {
  if (item.category !== 'process' || item.type !== 'log') return null;

  const processName = item.processName || item.name;

  if (item.content.startsWith('开始') && item.content.includes('执行')) {
    return {
      processName,
      status: 'running',
    };
  }

  if (item.content.startsWith('完成') && item.content.includes('执行')) {
    return {
      processName,
      status: 'completed',
    };
  }

  return null;
}

function getEacProcessBasePayload(
  round: 'firstRound' | 'secondRound'
): EacProcessBasePayload {
  const { globalConfig } = useConfig();
  const typedGlobalConfig = globalConfig as EacProcessGlobalConfig;
  const env: EacProcessEnv = typedGlobalConfig.env === 'test' ? 'test' : 'dev';
  const configuredPayload =
    typedGlobalConfig.eacProcessOntologyConfigs?.[env]?.[round] ||
    typedGlobalConfig.eacProcessOntologyConfigs?.dev?.[round];

  return {
    ...DEFAULT_EAC_PROCESS_ONTOLOGY_CONFIGS[env][round],
    ...configuredPayload,
  };
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
